const ONESIGNAL_SDK_URL = "https://cdn.onesignal.com/sdks/OneSignalSDK.js";
const SDK_LOAD_TIMEOUT_MS = 10000;

const APP_IDS_BY_SUBDOMAIN = {
  ice: "34c5c2a9-42c4-4c96-99b6-29d3d4cae965",
  eci: "967b2a3c-b2b0-4586-91a7-dd9ce716881d",
  smartsecure: "b931421f-8ac0-4dd0-b8bd-7aa725eae9f9",
  ugamer: "1046eea6-d6a0-4cee-9b20-a0413477d1e8",
  dem: "921ab717-864d-496c-b758-0303918f3bc8",
};

let sdkLoadPromise = null;

function ensureOneSignalStub() {
  window.OneSignal = window.OneSignal || [];
  return window.OneSignal;
}

export function isOneSignalSdkLoaded() {
  const oneSignal = window.OneSignal;
  return oneSignal != null && typeof oneSignal.init === "function";
}

export function loadOneSignalSdk() {
  if (isOneSignalSdkLoaded()) {
    return Promise.resolve(window.OneSignal);
  }
  if (sdkLoadPromise) {
    return sdkLoadPromise;
  }

  ensureOneSignalStub();

  sdkLoadPromise = new Promise((resolve) => {
    const finish = (result) => {
      resolve(result);
    };

    const timeoutId = setTimeout(() => finish(null), SDK_LOAD_TIMEOUT_MS);

    const onReady = () => {
      clearTimeout(timeoutId);
      finish(isOneSignalSdkLoaded() ? window.OneSignal : null);
    };

    const existing = document.querySelector(`script[src="${ONESIGNAL_SDK_URL}"]`);
    if (existing) {
      existing.addEventListener("load", onReady, { once: true });
      existing.addEventListener("error", () => onReady(), { once: true });
      if (isOneSignalSdkLoaded()) {
        onReady();
      }
      return;
    }

    const script = document.createElement("script");
    script.src = ONESIGNAL_SDK_URL;
    script.async = true;
    script.onload = onReady;
    script.onerror = onReady;
    document.head.appendChild(script);
  });

  return sdkLoadPromise;
}

export function pushOneSignal(callback) {
  try {
    const oneSignal = ensureOneSignalStub();
    if (typeof oneSignal.push === "function") {
      oneSignal.push(callback);
    }
  } catch (error) {
    console.warn("OneSignal: push callback failed", error);
  }
}

export function runOneSignalSafe(fn) {
  if (!isOneSignalSdkLoaded()) {
    return;
  }
  try {
    fn(window.OneSignal);
  } catch (error) {
    console.warn("OneSignal: operation failed", error);
  }
}

export function setOneSignalExternalUserId(userId) {
  if (userId == null) {
    return;
  }
  pushOneSignal(() => {
    runOneSignalSafe((OneSignal) => {
      if (typeof OneSignal.setExternalUserId === "function") {
        OneSignal.setExternalUserId(String(userId));
      }
    });
  });
}

export function setupOneSignalForSubdomain(subDomain) {
  const appId = APP_IDS_BY_SUBDOMAIN[subDomain];
  if (appId == null) {
    return;
  }

  loadOneSignalSdk().then(() => {
    pushOneSignal(() => {
      runOneSignalSafe((OneSignal) => {
        OneSignal.init({
          appId,
          subdomainName: subDomain,
        });

        if (typeof OneSignal.isPushNotificationsEnabled !== "function") {
          return;
        }

        OneSignal.isPushNotificationsEnabled()
          .then((isEnabled) => {
            if (isEnabled || typeof OneSignal.isOptedOut !== "function") {
              return;
            }
            OneSignal.isOptedOut((isOptedOut) => {
              try {
                if (isOptedOut && typeof OneSignal.setSubscription === "function") {
                  OneSignal.setSubscription(true);
                } else if (typeof OneSignal.showNativePrompt === "function") {
                  OneSignal.showNativePrompt();
                }
              } catch (error) {
                console.warn("OneSignal: subscription prompt failed", error);
              }
            });
          })
          .catch((error) => {
            console.warn("OneSignal: push status check failed", error);
          });
      });
    });
  });
}

export function disableOneSignalOnLogout(onComplete) {
  const done = typeof onComplete === "function" ? onComplete : () => {};
  let completed = false;

  const finish = () => {
    if (completed) {
      return;
    }
    completed = true;
    done();
  };

  setTimeout(finish, SDK_LOAD_TIMEOUT_MS);

  if (!isOneSignalSdkLoaded()) {
    finish();
    return;
  }

  runOneSignalSafe((OneSignal) => {
    if (typeof OneSignal.isPushNotificationsEnabled !== "function") {
      finish();
      return;
    }

    OneSignal.isPushNotificationsEnabled()
      .then((isEnabled) => {
        if (isEnabled && typeof OneSignal.setSubscription === "function") {
          OneSignal.setSubscription(false);
        }
        finish();
      })
      .catch(() => finish());
  });
}
